import { useEffect } from 'react'

/**
 * Locks page scroll for as long as the calling component is mounted.
 *
 * Modal/dialog overlays are `position: fixed` and scroll internally (via
 * `overflow-y-auto`) so their own top stays reachable on small screens. Without this
 * lock the underlying page can *also* scroll behind the overlay, producing two
 * independent scrollable regions — visually a "double scrollbar" — and scroll
 * gestures over the dimmed backdrop area move the page instead of the dialog.
 *
 * Just toggling `overflow: hidden` on `body` is not enough on real mobile browsers:
 * if the page was already scrolled when the modal opens, disabling scroll makes the
 * browser's collapsing address bar snap back into view, which shifts the viewport
 * *after* the `fixed` overlay has already been positioned — pushing its top out of
 * view until the user scrolls. Pinning `body` itself to `position: fixed` at the
 * current scroll offset keeps the viewport stable for the whole time the modal is
 * open, avoiding that shift. The scroll position is restored on unmount.
 */
export function useBodyScrollLock() {
  useEffect(() => {
    const { body } = document
    const scrollY = window.scrollY
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    }

    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflow = 'hidden'

    return () => {
      body.style.position = previous.position
      body.style.top = previous.top
      body.style.left = previous.left
      body.style.right = previous.right
      body.style.width = previous.width
      body.style.overflow = previous.overflow
      window.scrollTo(0, scrollY)
    }
  }, [])
}
